<script setup>
import { computed, inject, ref } from 'vue'
import FormButton from './FormButton.vue';

const props = defineProps({
  label: {
    type: String,
  },
  size: {
    type: String,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
  centered: {
    type: Boolean,
    default: false,
  },
  customClasses: {
    type: String,
  },
  type: {
    type: String,
    default: 'button'
  },
  to: {
    type: String,
  },
  onClick: Function
})

const groupType = inject('tabGroupType', ref(null))
const groupCols = inject('tabGroupCols', ref(null))
const groupSize = inject('tabGroupSize', ref('md'))

const resolvedType = computed(() => {
  // Prefer explicit item type; inherit segmented from parent group when using defaults.
  if (props.type && props.type !== 'button') return props.type
  if (groupType?.value === 'segmented') return 'segmented'
  return props.type
})

// Item size wins when set explicitly; otherwise inherit from TabGroup.
const resolvedSize = computed(() => props.size ?? groupSize?.value ?? 'md')

const segmentedSizeClasses = computed(() => {
  switch (resolvedSize.value) {
    case 'lg':
      return 'h-10 text-sm px-3'
    case 'sm':
      return 'h-7 text-tiny px-2'
    case 'xs':
      return 'h-6 text-tiny px-2'
    default:
      return 'h-8 text-tiny px-3'
  }
})

const activeClasses = computed(() => {
  if (resolvedType.value === 'segmented') {
    return props.isActive
      ? 'bg-surface-4 text-white shadow-sm'
      : 'text-surface-8 hover:text-white hover:bg-surface-3'
  }
  if (resolvedType.value === 'buttons-3') return props.isActive ? 'bg-surface-5 hover:text-white hover:bg-surface-5' : 'bg-surface-3'
  if (resolvedType.value === 'line') return props.isActive ? 'text-white border-white' : 'border-transparent'
  return props.isActive ? 'bg-surface-3' : 'bg-transparent'
})

const tabClasses = computed(() => {
  if (resolvedType.value === 'segmented') {
    const equalWidth = !!groupCols?.value
    return [
      'font-medium rounded-md transition-all text-nowrap leading-none flex items-center justify-center hover:no-underline',
      segmentedSizeClasses.value,
      equalWidth ? 'w-full' : '',
      props.disabled ? 'opacity-40 cursor-not-allowed hover:bg-transparent' : '',
      activeClasses.value,
    ].filter(Boolean).join(' ')
  }
  const buttonClasses = `h-8 p-2 ${props.centered ? 'justify-center' : 'justify-start'} ${props.disabled ? '' : 'hover:bg-surface-3'} hover:no-underline rounded text-surface-9 hover:text-white text-nowrap leading-none text-tiny flex items-center w-full ${activeClasses.value}`
  const lineClasses = `h-8 py-2 border-b-2 ${props.disabled ? 'text-surface-7' : 'hover:border-white hover:text-white text-surface-9'} hover:no-underline text-nowrap leading-none text-sm flex items-center w-full capitalize -mb-px ${props.isActive ? 'text-white border-white' : 'border-transparent'}`
  const buttons3Classes = `h-8 p-2 ${props.centered ? 'justify-center' : 'justify-start'} ${props.disabled ? '' : 'hover:bg-surface-4'} hover:no-underline rounded text-surface-9 hover:text-white text-nowrap leading-none text-tiny flex items-center w-full ${activeClasses.value}`
  const tabClasses = `flex h-8 items-center text-sm rounded capitalize ${props.centered ? 'justify-center' : 'justify-start'} px-2 w-full hover:bg-surface-4 ${activeClasses.value}`
  if (resolvedType.value === 'line') return lineClasses
  if (resolvedType.value === 'tab') return tabClasses
  if (resolvedType.value === 'buttons-3') return buttons3Classes
  return buttonClasses
})

const itemClass = computed(() => (
  resolvedType.value === 'segmented' && groupCols?.value ? 'min-w-0' : ''
))
</script>

<template>
  <li :class="[itemClass, 'list-none']">
    <FormButton :customClasses="customClasses ? customClasses : tabClasses" :type="type" :size="resolvedSize" :to="to" :label="label" :disabled="props.disabled" @click="onClick" >
      <slot />
    </FormButton>
  </li>
</template>
<style>
</style>
